import { InfoItem, Section } from "features/pregnancy/components/ConsultationLayout";
import {
  FETAL_ENGAGEMENT_OPTIONS,
  FETAL_PRESENTATION_OPTIONS,
  FOETAL_MOVEMENT_OPTIONS,
  UTERINE_CONTRACTIONS_OPTIONS,
} from "features/pregnancy/constants/antenatalDropdownOptions";
import {
  URINALYSIS_GLUCOSE_OPTIONS,
  URINALYSIS_NITRITES_OPTIONS,
  URINALYSIS_PROTEIN_OPTIONS,
} from "features/pregnancy/constants/urinalysisOptions";
import { formatDoctorName } from "features/pregnancy/utils/formatDoctorName";
import { formatPositiveNegative } from "features/pregnancy/utils/formatPositiveNegative";
import { getFoetalHeartLabel } from "features/pregnancy/utils/normaliseFoetalHeart";

import style from "./PregnancyAnteNatalConsultationContent.module.css";

// Map a stored value back to its option label (same lists the form uses).
const labelFor = (options, value) =>
  value == null || value === ""
    ? null
    : options.find((option) => option.value === value)?.label ?? value;

export const PregnancyAnteNatalConsultationContent = ({ consultation }) => {
  const doctorName = formatDoctorName(consultation.pregnancy?.conductedByStaffMember);

  const fundalHeight = consultation.fundalHeightCm ?? consultation.symphysis;
  const bp = consultation.bloodPressureSystolic && consultation.bloodPressureDiastolic
    ? `${consultation.bloodPressureSystolic}/${consultation.bloodPressureDiastolic}`
    : consultation.bloodPressure || null;

  const foetalObservations = consultation.foetalObservations ?? [];

  return (
    <div className={style.contentRoot}>
      <div className={style.sectionFirst}>
        <p className={style.sectionTitle}>Doctor</p>
        <p className={style.singleValue}>{doctorName}</p>
      </div>

      <Section title="Gestation">
        <InfoItem label="Gestation (weeks)" value={consultation.gestationWeeks != null ? String(consultation.gestationWeeks) : null} />
        <InfoItem label="Gestation (days)" value={consultation.gestationDays != null ? String(consultation.gestationDays) : null} />
      </Section>

      <Section title="Maternal Exam">
        <InfoItem label="BP" value={bp} />
        <InfoItem label="Maternal weight" value={consultation.maternalWeightKg ? `${consultation.maternalWeightKg} kg` : null} />
        <InfoItem label="Height" value={consultation.maternalHeightCm ? `${consultation.maternalHeightCm} cm` : null} />
        <InfoItem label="BMI" value={consultation.bmi ? String(consultation.bmi) : null} />
        <InfoItem label="Ankle oedema" value={consultation.ankleOedema} />
        <InfoItem label="Uterine contractions" value={labelFor(UTERINE_CONTRACTIONS_OPTIONS, consultation.uterineContractions)} />
        {!!fundalHeight && (
          <InfoItem label="Fundal height" value={`${fundalHeight} cm`} />
        )}
        <InfoItem label="Haemoglobin" value={consultation.haemoglobin ? `${consultation.haemoglobin} g/dL` : null} />
        <InfoItem label="Date of next visit" value={consultation.dateOfNextVisitWeeks ? `${consultation.dateOfNextVisitWeeks} weeks` : null} />
      </Section>

      <Section title="Urinalysis">
        <InfoItem label="Glucose" value={labelFor(URINALYSIS_GLUCOSE_OPTIONS, consultation.urinalysisGlucose)} />
        <InfoItem label="Protein" value={labelFor(URINALYSIS_PROTEIN_OPTIONS, consultation.urinalysisProtein)} />
        <InfoItem label="Blood" value={formatPositiveNegative(consultation.urinalysisBlood)} />
        <InfoItem label="Ketones" value={formatPositiveNegative(consultation.urinalysisKetones)} />
        <InfoItem label="Leucocytes" value={formatPositiveNegative(consultation.urinalysisLeucocytes)} />
        {/*
          * Read through the option list, not formatPositiveNegative, which reports a missing
          * value as "Negative". Nitrites was a tick box whose unticked state saved null, so that
          * would show a negative result on every visit that simply never recorded one. A blank
          * falls through to InfoItem's dash, which is what was actually recorded.
          */}
        <InfoItem label="Nitrites" value={labelFor(URINALYSIS_NITRITES_OPTIONS, consultation.urinalysisNitrites)} />
      </Section>

      {foetalObservations.length > 0 && (
        <Section title="Foetal Exam">
          {foetalObservations.map((obs, index) => {
            const fields = [
              { label: "Presentation", value: labelFor(FETAL_PRESENTATION_OPTIONS, obs.presentation) },
              { label: "Engagement", value: labelFor(FETAL_ENGAGEMENT_OPTIONS, obs.engagement) },
              // Reads through the normaliser, so a visit stored as "yes" still shows "Present" (KIN-2072).
              { label: "Heart heard (doppler)", value: getFoetalHeartLabel(obs.heartHeardWithDoppler) },
              { label: "Heart rate", value: obs.heartRateBpm ? `${obs.heartRateBpm} bpm` : null },
              { label: "Estimated weight", value: obs.estimatedWeightGrams ? `${obs.estimatedWeightGrams} g` : null },
              { label: "Activity", value: labelFor(FOETAL_MOVEMENT_OPTIONS, obs.activity) },
            ].filter((field) => field.value != null);

            return (
              <div key={obs.id ?? obs.foetusId} className={style.foetalObsBlock}>
                <p className={style.foetalObsLabel}>
                  Baby {obs.pregnancyFoetus?.foetusIdentifier ?? index + 1}
                </p>
                {fields.map((field) => (
                  <InfoItem key={field.label} label={field.label} value={field.value} />
                ))}
                {!!obs.notes && (
                  <InfoItem label="Notes" value={obs.notes} fullWidth />
                )}
              </div>
            );
          })}
        </Section>
      )}

      <Section title="Clinical notes">
        <InfoItem label="Impression" value={consultation.impression} fullWidth />
        <InfoItem label="Plan" value={consultation.plan} fullWidth />
        <InfoItem label="Additional Info" value={consultation.additionalInfo} fullWidth />
      </Section>
    </div>
  );
};
